import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import { supabase } from '../lib/supabase';
import AppHeader from '../components/AppHeader';
import { 
  Users, CreditCard, TrendingUp, Gift, Calendar, 
  DollarSign, Activity, Search, AlertCircle 
} from 'lucide-react';

const AdminPage = () => {
  const { user } = useAuth();
  const { t, dir } = useLanguage();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'users' | 'purchases' | 'usage' | 'lots'>('users');
  const [stats, setStats] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [purchases, setPurchases] = useState<any[]>([]);
  const [usages, setUsages] = useState<any[]>([]);
  const [creditLots, setCreditLots] = useState<any[]>([]);
  
  // Grant modal
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [grantAmount, setGrantAmount] = useState(50);
  const [grantDuration, setGrantDuration] = useState(30);

  const fetchDashboardStats = async () => {
    try {
      // Total purchases (successful payments)
      const { data: purchases } = await supabase
        .from('payment_transactions')
        .select('price_amount, created_at')
        .eq('status', 'success');
      
      const totalPurchases = purchases?.length || 0;
      const totalRevenue = purchases?.reduce((sum, p) => sum + Number(p.price_amount || 0), 0) || 0;
      
      // Today's stats
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayPurchases = purchases?.filter(p => 
        new Date(p.created_at) >= today
      ).length || 0;
      const todayRevenue = purchases?.filter(p => 
        new Date(p.created_at) >= today
      ).reduce((sum, p) => sum + Number(p.price_amount || 0), 0) || 0;
      
      // Credits
      const { data: creditLots } = await supabase
        .from('credit_lots')
        .select('amount');
      const totalCreditsInSystem = creditLots?.reduce((sum, l) => sum + Number(l.amount || 0), 0) || 0;
      
      // Credits used today
      const { data: todayUsage } = await supabase
        .from('credit_usages')
        .select('amount')
        .gte('created_at', today.toISOString());
      const creditsUsedToday = todayUsage?.reduce((sum, u) => sum + Math.abs(u.amount || 0), 0) || 0;
      
      // Total credits used
      const { data: allUsage } = await supabase
        .from('credit_usages')
        .select('amount');
      const totalCreditsUsed = allUsage?.reduce((sum, u) => sum + Math.abs(u.amount || 0), 0) || 0;
      
      // Total users
      const { count: totalUsers } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true });
      
      setStats({
        totalUsers: totalUsers || 0,
        totalPurchases,
        totalRevenue,
        todayPurchases,
        todayRevenue,
        totalCreditsInSystem,
        creditsUsedToday,
        totalCreditsUsed
      });
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data: usersData } = await supabase
        .from('users')
        .select('id, email, username, created_at')
        .order('created_at', { ascending: false })
        .limit(100); // Limit to 100 users initially

      const usersWithCredits = await Promise.all(
        (usersData || []).map(async (u) => {
          // Get available credits
          const { data: credits } = await supabase.rpc(
            'get_user_available_credits',
            { p_user_id: u.id }
          );
          
          // Get total granted
          const { data: lots } = await supabase
            .from('credit_lots')
            .select('amount')
            .eq('user_id', u.id);
          const totalGranted = lots?.reduce((sum, l) => sum + Number(l.amount || 0), 0) || 0;
          
          // Get total used
          const { data: usages } = await supabase
            .from('credit_usages')
            .select('amount')
            .eq('user_id', u.id);
          const totalUsed = usages?.reduce((sum, u) => sum + Math.abs(u.amount || 0), 0) || 0;
          
          return {
            id: u.id,
            email: u.email,
            username: u.username,
            availableCredits: credits || 0,
            totalGranted,
            totalUsed,
            createdAt: u.created_at
          };
        })
      );
      
      setUsers(usersWithCredits);
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPurchases = async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('payment_transactions')
        .select('*, users!inner(email, username)')
        .eq('status', 'success')
        .order('created_at', { ascending: false })
        .limit(100);
      
      const purchasesWithUsers = (data || []).map((p: any) => ({
        ...p,
        user: p.users
      }));
      
      setPurchases(purchasesWithUsers);
    } catch (err) {
      console.error('Error fetching purchases:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsageLogs = async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('credit_usages')
        .select('*, users!inner(email, username)')
        .order('created_at', { ascending: false })
        .limit(100);
      
      const usagesWithUsers = (data || []).map((u: any) => ({
        ...u,
        user: u.users
      }));
      
      setUsages(usagesWithUsers);
    } catch (err) {
      console.error('Error fetching usage logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCreditLots = async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('credit_lots')
        .select('id, user_id, amount, remaining, source, reference, created_at, expires_at, users!inner(email, username)')
        .order('created_at', { ascending: false })
        .limit(500);

      const now = new Date();
      const lotsWithUsers = (data || []).map((l: any) => {
        const expires = l.expires_at ? new Date(l.expires_at) : null;
        const ms = expires ? (expires.getTime() - now.getTime()) : null;
        const daysRemaining = ms !== null ? Math.ceil(ms / (1000 * 60 * 60 * 24)) : null;

        return {
          ...l,
          user: l.users,
          daysRemaining,
          isExpired: expires ? expires < now : false,
        };
      });

      setCreditLots(lotsWithUsers);
    } catch (err) {
      console.error('Error fetching credit lots:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user || user.email !== 'ahmedaldakheel02@gmail.com') {
      navigate('/');
      return;
    }
    // Load stats and users tab immediately
    fetchDashboardStats();
    fetchUsers();
  }, [user, navigate]);

  // Load data for other tabs when they become active
  useEffect(() => {
    if (!user || user.email !== 'ahmedaldakheel02@gmail.com') return;
    
    if (activeTab === 'purchases' && purchases.length === 0) {
      fetchPurchases();
    } else if (activeTab === 'usage' && usages.length === 0) {
      fetchUsageLogs();
    } else if (activeTab === 'lots' && creditLots.length === 0) {
      fetchCreditLots();
    }
  }, [activeTab, user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchDashboardStats(),
        fetchUsers(),
        fetchPurchases(),
        fetchUsageLogs(),
        fetchCreditLots()
      ]);
    } catch (err) {
      console.error('Error fetching admin data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGrantCredits = async () => {
    if (!selectedUserId || grantAmount <= 0) {
      alert(t('pages.admin.invalidData'));
      return;
    }

    try {
      const { error } = await supabase.rpc('add_credits', {
        p_user_id: selectedUserId,
        p_amount: grantAmount,
        p_expires_in_days: grantDuration,
        p_source: 'admin_grant',
        p_reference: t('pages.admin.grantReference', { amount: grantAmount })
      });

      if (error) throw error;

      alert(t('pages.admin.creditsAdded'));
      setShowGrantModal(false);
      // Refresh only users tab and stats
      fetchUsers();
      fetchDashboardStats();
    } catch (err) {
      console.error('Error granting credits:', err);
      alert(t('pages.admin.errorAddingCredits'));
    }
  };

  const filteredUsers = users.filter(u => 
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.username?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl">{t('pages.admin.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" dir={dir}>
      <AppHeader />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">{t('pages.admin.title')}</h1>
          <p className="text-gray-400">{t('pages.admin.subtitle')}</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Total Revenue */}
            <div className="bg-gradient-to-br from-green-900/40 to-green-800/40 border-2 border-green-500/50 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <DollarSign className="w-8 h-8 text-green-400" />
                <span className="text-green-400 text-sm">{t('pages.admin.totalRevenue')}</span>
              </div>
              <div className="text-3xl font-bold text-white mb-1">
                ${stats.totalRevenue.toFixed(2)}
              </div>
              <div className="text-green-300 text-sm">{stats.totalPurchases} {t('pages.admin.purchases')}</div>
            </div>

            {/* Today's Revenue */}
            <div className="bg-gradient-to-br from-blue-900/40 to-blue-800/40 border-2 border-blue-500/50 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <TrendingUp className="w-8 h-8 text-blue-400" />
                <span className="text-blue-400 text-sm">{t('pages.admin.todayRevenue')}</span>
              </div>
              <div className="text-3xl font-bold text-white mb-1">
                ${stats.todayRevenue.toFixed(2)}
              </div>
              <div className="text-blue-300 text-sm">{stats.todayPurchases} {t('pages.admin.operations')}</div>
            </div>

            {/* Total Credits in System */}
            <div className="bg-gradient-to-br from-purple-900/40 to-purple-800/40 border-2 border-purple-500/50 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <CreditCard className="w-8 h-8 text-purple-400" />
                <span className="text-purple-400 text-sm">{t('pages.admin.availableCredits')}</span>
              </div>
              <div className="text-3xl font-bold text-white mb-1">
                {stats.totalCreditsInSystem.toLocaleString('en-US')}
              </div>
              <div className="text-purple-300 text-sm">{t('pages.admin.creditsInSystem')}</div>
            </div>

            {/* Credits Used Today */}
            <div className="bg-gradient-to-br from-orange-900/40 to-orange-800/40 border-2 border-orange-500/50 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <Activity className="w-8 h-8 text-orange-400" />
                <span className="text-orange-400 text-sm">{t('pages.admin.todayUsage')}</span>
              </div>
              <div className="text-3xl font-bold text-white mb-1">
                {stats.creditsUsedToday}
              </div>
              <div className="text-orange-300 text-sm">{t('pages.admin.credits')}</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl mb-6">
          <div className="flex border-b border-zinc-800">
            <button
              onClick={() => setActiveTab('users')}
              className={`flex-1 py-4 px-6 font-semibold transition-colors inline-flex items-center justify-center gap-2 ${
                activeTab === 'users'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-zinc-800'
              }`}
            >
              <Users className="w-5 h-5" />
              {t('pages.admin.users')}
            </button>
            <button
              onClick={() => setActiveTab('purchases')}
              className={`flex-1 py-4 px-6 font-semibold transition-colors inline-flex items-center justify-center gap-2 ${
                activeTab === 'purchases'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-zinc-800'
              }`}
            >
              <DollarSign className="w-5 h-5" />
              {t('pages.admin.purchasesTab')}
            </button>
            <button
              onClick={() => setActiveTab('usage')}
              className={`flex-1 py-4 px-6 font-semibold transition-colors inline-flex items-center justify-center gap-2 ${
                activeTab === 'usage'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-zinc-800'
              }`}
            >
              <Activity className="w-5 h-5" />
              {t('pages.admin.usageTab')}
            </button>
            <button
              onClick={() => setActiveTab('lots')}
              className={`flex-1 py-4 px-6 font-semibold transition-colors inline-flex items-center justify-center gap-2 ${
                activeTab === 'lots'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-zinc-800'
              }`}
            >
              <Calendar className="w-5 h-5" />
              {t('pages.admin.creditLotsTab')}
            </button>
          </div>
        </div>

        {/* Users Tab */}
        {activeTab === 'users' && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden mb-6">
          <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Users className="w-6 h-6 text-blue-400" />
              <h2 className="text-xl font-bold text-white">{t('pages.admin.users')}</h2>
              <span className="text-gray-400">({users.length})</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="w-5 h-5 text-gray-400 absolute right-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder={t('pages.admin.search')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="bg-zinc-800 border border-zinc-700 rounded-lg px-4 pr-10 py-2 text-white w-64 focus:outline-none focus:border-blue-500"
                />
              </div>
              <button
                onClick={() => setShowGrantModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-bold inline-flex items-center gap-2"
              >
                <Gift className="w-5 h-5" />
                {t('pages.admin.addCredits')}
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-zinc-800">
                <tr>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase">{t('pages.admin.user')}</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase">{t('pages.admin.availableCreditsCol')}</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase">{t('pages.admin.granted')}</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase">{t('pages.admin.spent')}</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase">{t('pages.admin.createdAt')}</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase">{t('pages.admin.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {filteredUsers.map((u) => (
                  <tr key={u.id} className="hover:bg-zinc-800/50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-white font-semibold">{u.email}</div>
                        <div className="text-gray-500 text-sm">{u.username || '-'}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-white font-bold">
                      {u.availableCredits.toLocaleString('en-US')}
                    </td>
                    <td className="px-6 py-4 text-blue-400">
                      {u.totalGranted.toLocaleString('en-US')}
                    </td>
                    <td className="px-6 py-4 text-red-400">
                      {u.totalUsed.toLocaleString('en-US')}
                    </td>
                    <td className="px-6 py-4 text-gray-400 text-sm">
                      {new Date(u.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => {
                          setSelectedUserId(u.id);
                          setShowGrantModal(true);
                        }}
                        className="text-blue-400 hover:text-blue-300 text-sm font-semibold"
                      >
                        {t('pages.admin.addCreditsAction')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {/* Purchases Tab */}
        {activeTab === 'purchases' && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden mb-6">
          <div className="p-6 border-b border-zinc-800">
            <div className="flex items-center gap-4">
              <DollarSign className="w-6 h-6 text-green-400" />
              <h2 className="text-xl font-bold text-white">{t('pages.admin.purchasesTab')}</h2>
              <span className="text-gray-400">({purchases.length})</span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-zinc-800">
                <tr>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase">{t('pages.admin.transactionNumber')}</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase">{t('pages.admin.user')}</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase">{t('pages.admin.amount')}</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase">{t('pages.admin.creditsCol')}</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase">{t('pages.admin.date')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {purchases.map((p) => (
                  <tr key={p.id} className="hover:bg-zinc-800/50">
                    <td className="px-6 py-4 text-white font-semibold">
                      #{p.transaction_number}
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-white font-semibold">{p.user?.email || p.user_id}</div>
                        <div className="text-gray-500 text-sm">{p.user?.username || '-'}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-green-400 font-bold">
                      ${p.price_amount}
                    </td>
                    <td className="px-6 py-4 text-blue-400 font-bold">
                      +{p.credits_amount}
                    </td>
                    <td className="px-6 py-4 text-gray-400 text-sm">
                      {new Date(p.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {/* Usage Tab */}
        {activeTab === 'usage' && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden mb-6">
          <div className="p-6 border-b border-zinc-800">
            <div className="flex items-center gap-4">
              <Activity className="w-6 h-6 text-orange-400" />
              <h2 className="text-xl font-bold text-white">{t('pages.admin.creditLogs')}</h2>
              <span className="text-gray-400">({usages.length})</span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-zinc-800">
                <tr>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase">{t('pages.admin.user')}</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase">{t('pages.admin.creditsCol')}</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase">{t('pages.admin.reason')}</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase">{t('pages.admin.dateTime')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {usages.map((u) => (
                  <tr key={u.id} className="hover:bg-zinc-800/50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-white font-semibold">{u.user?.email || u.user_id}</div>
                        <div className="text-gray-500 text-sm">{u.user?.username || '-'}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-red-400 font-bold">
                      -{Math.abs(u.amount)}
                    </td>
                    <td className="px-6 py-4 text-gray-400 text-sm max-w-xs truncate">
                      {u.reason}
                    </td>
                    <td className="px-6 py-4 text-gray-400 text-sm">
                      {new Date(u.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {/* Credit Lots Tab */}
        {activeTab === 'lots' && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden mb-6">
          <div className="p-6 border-b border-zinc-800">
            <div className="flex items-center gap-4">
              <Calendar className="w-6 h-6 text-cyan-400" />
              <h2 className="text-xl font-bold text-white">{t('pages.admin.creditLotsTitle')}</h2>
              <span className="text-gray-400">({creditLots.length})</span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-zinc-800">
                <tr>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase">{t('pages.admin.user')}</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase">{t('pages.admin.source')}</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase">{t('pages.admin.amount')}</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase">{t('pages.admin.remaining')}</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase">{t('pages.admin.createdAtCol')}</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase">{t('pages.admin.expiresAt')}</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase">{t('pages.admin.daysRemaining')}</th>
                  <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase">{t('pages.admin.reference')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {creditLots.map((l) => (
                  <tr key={l.id} className="hover:bg-zinc-800/50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-white font-semibold">{l.user?.email || l.user_id}</div>
                        <div className="text-gray-500 text-sm">{l.user?.username || '-'}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-300 text-sm">{l.source || '-'}</td>
                    <td className="px-6 py-4 text-blue-400 font-bold">+{l.amount}</td>
                    <td className="px-6 py-4 text-yellow-400 font-bold">{l.remaining ?? '-'}</td>
                    <td className="px-6 py-4 text-gray-400 text-sm">{new Date(l.created_at).toLocaleString('en-US')}</td>
                    <td className="px-6 py-4 text-gray-400 text-sm">{l.expires_at ? new Date(l.expires_at).toLocaleString('en-US') : '-'}</td>
                    <td className="px-6 py-4">
                      {l.expires_at === null ? (
                        <span className="text-gray-400 text-sm">{t('pages.admin.neverExpires')}</span>
                      ) : l.isExpired ? (
                        <span className="text-red-400 font-bold">{t('pages.admin.expired')}</span>
                      ) : (
                        <span className="text-green-400 font-bold">{t('pages.admin.days', { days: l.daysRemaining })}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-400 text-xs max-w-[280px] truncate" title={l.reference || ''}>{l.reference || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {/* Grant Modal */}
        {showGrantModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-zinc-900 border-2 border-blue-500/50 rounded-xl p-6 max-w-md w-full">
              <h3 className="text-2xl font-bold text-white mb-6">{t('pages.admin.grantModalTitle')}</h3>
              
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">{t('pages.admin.amountLabel')}</label>
                  <input
                    type="number"
                    value={grantAmount}
                    onChange={(e) => setGrantAmount(Number(e.target.value))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                    min="1"
                  />
                </div>
                
                <div>
                  <label className="block text-gray-400 text-sm mb-2">{t('pages.admin.durationLabel')}</label>
                  <input
                    type="number"
                    value={grantDuration}
                    onChange={(e) => setGrantDuration(Number(e.target.value))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                    min="1"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowGrantModal(false)}
                  className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white px-6 py-3 rounded-lg font-bold transition-colors"
                >
                  {t('pages.admin.cancel')}
                </button>
                <button
                  onClick={handleGrantCredits}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-bold transition-colors inline-flex items-center justify-center gap-2"
                >
                  <Gift className="w-5 h-5" />
                  {t('pages.admin.add')}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPage;
